import {
  createEffect,
  createMemo,
  createSignal,
  For,
  on,
  onCleanup,
  Show,
} from "solid-js";
import {
  formatDateTime,
  formatNumber,
  formatShare,
  timeTicks,
} from "../format.ts";
import { t } from "../i18n.ts";
import { isFull, pan, select, zoomAt, type Span } from "../chartZoom.ts";

export interface ChartPoint {
  /** Epoch milliseconds. */
  t: number;
  /** Null is a gap: lines break there, bars are left out. */
  v: number | null;
  /** Share of the bucket that had data (0–1); below 1 the tooltip says the value is from an incomplete interval. */
  c?: number;
}

export interface ChartSeries {
  key: string;
  label: string;
  color: string;
  kind: "line" | "area" | "bar";
  points: ChartPoint[];
  dashed?: boolean;
  /** A thin dotted reference line (e.g. the typical course). */
  dotted?: boolean;
}

/** What a mouse drag does: select a span to zoom into, pan the view, or place an annotation with a click. */
export type ChartMode = "select" | "pan" | "note";

interface Props {
  series: ChartSeries[];
  from: Date;
  to: Date;
  unit: string;
  locale: string;
  /** Bucket size of bar series in ms. */
  bucketMs?: number;
  height?: number;
  emptyText: string;
  /** Spans without (complete) data, hatched. */
  gaps?: { from: number; to: number }[];
  gapLabel?: string;
  /** A vertical "now" line when it falls inside the range. */
  now?: number;
  nowLabel?: string;
  /** Horizontal threshold lines (e.g. an alert rule's), always within the value axis. */
  thresholds?: { value: number; label: string }[];
  /** Spans to highlight, e.g. when a value was beyond a threshold. */
  bands?: { from: number; to: number; color?: string }[];
  /** The visible span, when several charts share it (with onView); otherwise the chart keeps its own. */
  view?: Span | null;
  onView?: (view: Span | null) => void;
  mode?: ChartMode;
  /** A click in "note" mode: where to place an annotation. */
  onNote?: (at: number) => void;
  /** A click without dragging in "select" mode (e.g. a day at day resolution, to see its hours). */
  onDrill?: (at: number) => void;
  /** Notes pinned to moments, drawn as labelled lines. */
  annotations?: { at: number; text: string; who: string }[];
  /** Events, drawn as markers at the bottom that can be clicked. */
  markers?: { at: number; label: string; onClick?: () => void }[];
  /** A series to bring forward; the others fade. */
  highlight?: string | null;
  /** The value axis follows the data instead of starting at zero (voltage, frequency, power factor). */
  tight?: boolean;
  /** Leave out the legend and the zoom line (the page draws its own). */
  bare?: boolean;
  /** The drawn SVG, e.g. to export it as an image. */
  onSvg?: (svg: SVGSVGElement) => void;
}

const W = 800;
const PAD = { l: 52, r: 12, t: 12, b: 26 };

/** Round tick values covering [min, max] with a tick at or beyond both ends, including zero unless <paramref name="tight"/>. */
function ticks(min: number, max: number, count = 5, tight = false): number[] {
  const pad = tight
    ? Math.max((max - min) * 0.1, Math.abs(max) * 0.005, 0.01)
    : 0;
  const lo = tight ? min - pad : Math.min(0, min);
  const hi = tight ? max + pad : Math.max(0, max);
  const span = hi - lo || 1;
  const raw = span / count;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const step =
    [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw) ?? raw;
  const out: number[] = [];
  const top = Math.ceil(hi / step - 1e-9) * step;
  for (
    let v = Math.floor(lo / step + 1e-9) * step;
    v <= top + step * 0.001;
    v += step
  ) {
    out.push(Math.round(v / step) * step);
  }
  return out;
}

let chartIds = 0;

/**
 * Time-series chart in the prototype's style: thin lines, hatched gaps, a dashed cursor with a tooltip.
 * Drawn as SVG in a fixed coordinate space that scales with the card width. The time axis zooms like in the
 * prototype: wheel or pinch around the pointer, a dragged span, panning (one finger, the pan mode or Shift + drag),
 * a double click shows the whole range again. Annotations and events are drawn on the time axis.
 */
export function Chart(props: Props) {
  const h = () => props.height ?? 240;
  const clip = `chart-clip-${++chartIds}`;
  const [hover, setHover] = createSignal<number | null>(null);
  const full = (): Span => ({
    from: props.from.getTime(),
    to: props.to.getTime(),
  });
  const [own, setOwn] = createSignal<Span | null>(null);
  const controlled = () => props.onView !== undefined;
  const view = () => (controlled() ? (props.view ?? null) : own());
  const setView = (v: Span | null) =>
    controlled() ? props.onView!(v) : setOwn(v);
  // A new period shows its whole range.
  createEffect(
    on(
      () => [props.from.getTime(), props.to.getTime()],
      () => !controlled() && setOwn(null),
      { defer: true },
    ),
  );
  const shown = (): Span => view() ?? full();
  const t0 = () => shown().from;
  const t1 = () => shown().to;
  const x = (t: number) =>
    PAD.l + ((t - t0()) / (t1() - t0() || 1)) * (W - PAD.l - PAD.r);
  const inside = (t: number) => t >= t0() && t <= t1();
  // At least four buckets stay visible, and never less than an hour.
  const minSpan = () => Math.max(3_600_000, (props.bucketMs ?? 900_000) * 4);

  // The value axis follows what is visible.
  const values = createMemo(() =>
    props.series.flatMap((s) =>
      s.points
        .filter((p) => p.t >= t0() - (props.bucketMs ?? 0) && p.t <= t1())
        .map((p) => p.v)
        .filter((v): v is number => v !== null),
    ),
  );
  const axisValues = createMemo(() => [
    ...values(),
    ...(props.thresholds ?? []).map((th) => th.value),
  ]);
  const anyValue = createMemo(() =>
    props.series.some((s) => s.points.some((p) => p.v !== null)),
  );
  const yTicks = createMemo(() => {
    const v = axisValues();
    return v.length === 0
      ? [0, 1]
      : ticks(Math.min(...v), Math.max(...v), 5, props.tight === true);
  });
  const y = (v: number) => {
    const tk = yTicks();
    const lo = tk[0]!;
    const hi = tk[tk.length - 1]!;
    return PAD.t + (1 - (v - lo) / (hi - lo || 1)) * (h() - PAD.t - PAD.b);
  };

  const xTicks = createMemo(() => timeTicks(new Date(t0()), new Date(t1())));

  const path = (points: ChartPoint[], area: boolean) => {
    const parts: string[] = [];
    let run: ChartPoint[] = [];
    const flush = () => {
      if (run.length === 0) {
        return;
      }
      const line = run
        .map(
          (p, i) =>
            `${i === 0 ? "M" : "L"}${x(p.t).toFixed(1)},${y(p.v!).toFixed(1)}`,
        )
        .join("");
      parts.push(
        area
          ? `${line}L${x(run[run.length - 1]!.t).toFixed(1)},${y(0).toFixed(1)}L${x(run[0]!.t).toFixed(1)},${y(0).toFixed(1)}Z`
          : line,
      );
      run = [];
    };
    for (const p of points) {
      if (p.v === null) {
        flush();
      } else {
        run.push(p);
      }
    }
    flush();
    return parts.join("");
  };

  const bars = createMemo(() => props.series.filter((s) => s.kind === "bar"));
  const bucket = () => props.bucketMs ?? 3600_000;
  const barWidth = () => {
    const slot = (bucket() / (t1() - t0() || 1)) * (W - PAD.l - PAD.r);
    return Math.max(1, (slot * 0.8) / Math.max(1, bars().length));
  };
  const faded = (key: string) =>
    props.highlight != null && props.highlight !== key;

  // Nearest timestamp across all series for the tooltip.
  const times = createMemo(() =>
    [...new Set(props.series.flatMap((s) => s.points.map((p) => p.t)))].sort(
      (a, b) => a - b,
    ),
  );
  const [svg, setSvg] = createSignal<SVGSVGElement>();
  createEffect(() => {
    const el = svg();
    if (el) {
      props.onSvg?.(el);
    }
  });
  /** Time under a screen x coordinate. */
  const timeAt = (clientX: number) => {
    const rect = svg()!.getBoundingClientRect();
    const px = ((clientX - rect.left) / rect.width) * W;
    return t0() + ((px - PAD.l) / (W - PAD.l - PAD.r)) * (t1() - t0());
  };
  const msPerPixel = () => {
    const rect = svg()!.getBoundingClientRect();
    return ((t1() - t0()) / (W - PAD.l - PAD.r)) * (W / rect.width);
  };
  const apply = (next: Span) => setView(isFull(next, full()) ? null : next);

  // Wheel: the page scrolls as usual; Ctrl (⌘) + wheel zooms around the pointer, which is also what a touchpad pinch
  // sends, and Ctrl + Shift + wheel pans. A plain wheel only shows for a moment how to zoom.
  const [wheelHint, setWheelHint] = createSignal(false);
  let wheelHintTimer: ReturnType<typeof setTimeout> | undefined;
  onCleanup(() => clearTimeout(wheelHintTimer));
  const onWheel = (e: WheelEvent) => {
    const delta = e.deltaY || e.deltaX;
    if (delta === 0) {
      return;
    }
    if (!e.ctrlKey && !e.metaKey) {
      setWheelHint(true);
      clearTimeout(wheelHintTimer);
      wheelHintTimer = setTimeout(() => setWheelHint(false), 1400);
      return;
    }
    e.preventDefault();
    if (e.shiftKey) {
      apply(pan(shown(), full(), (delta > 0 ? 0.1 : -0.1) * (t1() - t0())));
      return;
    }
    // A touchpad pinch sends small deltas: zoom in proportion, a mouse wheel notch by a quarter.
    const factor = Math.min(1.5, Math.max(0.66, Math.exp(delta * 0.01)));
    apply(zoomAt(shown(), full(), timeAt(e.clientX), factor, minSpan()));
  };
  // Buttons: zoom around the middle of the view, move it by a third.
  const zoomBy = (factor: number) => {
    const v = shown();
    apply(zoomAt(v, full(), (v.from + v.to) / 2, factor, minSpan()));
  };
  const panBy = (share: number) => {
    const v = shown();
    apply(pan(v, full(), share * (v.to - v.from)));
  };

  // Pointers: two fingers pinch, one finger (or the pan mode, or Shift) pans, a mouse drags a span to zoom into.
  const pointers = new Map<number, number>();
  let pinch: { distance: number; at: number; view: Span } | null = null;
  let drag: { x: number; view: Span } | null = null;
  let downAt: number | null = null;
  const [brush, setBrush] = createSignal<{ a: number; b: number } | null>(null);
  const onPointerDown = (e: PointerEvent) => {
    try {
      svg()!.setPointerCapture(e.pointerId);
    } catch {
      // A pointer that is no longer active cannot be captured; the gesture still works while it stays over the chart.
    }
    pointers.set(e.pointerId, e.clientX);
    downAt = e.clientX;
    if (pointers.size === 2) {
      const [a, b] = [...pointers.values()] as [number, number];
      pinch = {
        distance: Math.abs(a - b) || 1,
        at: timeAt((a + b) / 2),
        view: shown(),
      };
      drag = null;
      setBrush(null);
    } else if (
      e.pointerType === "mouse" &&
      props.mode !== "pan" &&
      !e.shiftKey
    ) {
      if (e.button === 0 && props.mode !== "note") {
        setBrush({ a: timeAt(e.clientX), b: timeAt(e.clientX) });
      }
    } else {
      drag = { x: e.clientX, view: shown() };
    }
  };
  const onPointerMove = (e: PointerEvent) => {
    if (!pointers.has(e.pointerId)) {
      return;
    }
    pointers.set(e.pointerId, e.clientX);
    if (pinch && pointers.size >= 2) {
      const [a, b] = [...pointers.values()] as [number, number];
      apply(
        zoomAt(
          pinch.view,
          full(),
          pinch.at,
          pinch.distance / (Math.abs(a - b) || 1),
          minSpan(),
        ),
      );
    } else if (drag) {
      apply(pan(drag.view, full(), (drag.x - e.clientX) * msPerPixel()));
    } else if (brush()) {
      setBrush({ a: brush()!.a, b: timeAt(e.clientX) });
    }
  };
  const onPointerUp = (e: PointerEvent) => {
    pointers.delete(e.pointerId);
    const clicked = downAt !== null && Math.abs(e.clientX - downAt) < 4;
    if (pointers.size < 2) {
      pinch = null;
    }
    if (pointers.size === 0) {
      drag = null;
      downAt = null;
      if (clicked && e.pointerType === "mouse" && props.mode === "note") {
        props.onNote?.(timeAt(e.clientX));
      }
      const b = brush();
      if (b) {
        const span = select(b.a, b.b, full(), minSpan());
        if (span) {
          apply(span);
        } else if (clicked) {
          props.onDrill?.(timeAt(e.clientX));
        }
        setBrush(null);
      }
    }
  };
  createEffect(() => {
    const el = svg();
    if (el) {
      el.addEventListener("wheel", onWheel, { passive: false });
      onCleanup(() => el.removeEventListener("wheel", onWheel));
    }
  });

  const onMove = (e: MouseEvent) => {
    const tm = timeAt(e.clientX);
    const all = times();
    if (all.length === 0) {
      return;
    }
    let best = all[0]!;
    for (const c of all) {
      if (Math.abs(c - tm) < Math.abs(best - tm)) {
        best = c;
      }
    }
    setHover(best);
  };

  const tip = createMemo(() => {
    const at = hover();
    if (at === null) {
      return null;
    }
    const rows = props.series.map((s) => ({
      s,
      p: s.points.find((p) => p.t === at),
    }));
    const note = (props.annotations ?? []).find(
      (a) => a.at >= at && a.at < at + bucket(),
    );
    return { at, rows, note };
  });
  const cursor = () =>
    brush()
      ? "col-resize"
      : props.mode === "pan"
        ? "grab"
        : props.mode === "note"
          ? "copy"
          : "crosshair";

  return (
    <Show
      when={anyValue()}
      fallback={<div class="chart-empty">{props.emptyText}</div>}
    >
      <div class="chart">
        <Show when={wheelHint()}>
          <div class="chart-wheel-hint">{t("chart.wheelHint")}</div>
        </Show>
        <svg
          ref={setSvg}
          viewBox={`0 0 ${W} ${h()}`}
          role="img"
          style={{
            "touch-action": "pan-y",
            cursor: cursor(),
            "user-select": "none",
          }}
          onMouseMove={onMove}
          onMouseLeave={() => setHover(null)}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onDblClick={() => setView(null)}
        >
          <defs>
            <clipPath id={clip}>
              <rect x={PAD.l} y={0} width={W - PAD.l - PAD.r} height={h()} />
            </clipPath>
            <pattern
              id="chart-hatch"
              width="5"
              height="5"
              patternUnits="userSpaceOnUse"
              patternTransform="rotate(45)"
            >
              <rect width="2" height="5" fill="var(--hatch)" />
            </pattern>
          </defs>
          <g clip-path={`url(#${clip})`}>
            <For each={props.bands ?? []}>
              {(b) => (
                <rect
                  x={x(b.from)}
                  width={Math.max(1, x(b.to) - x(b.from))}
                  y={PAD.t}
                  height={h() - PAD.t - PAD.b}
                  fill={b.color ?? "var(--crit)"}
                  opacity="0.14"
                />
              )}
            </For>
            <For each={props.gaps ?? []}>
              {(g) => (
                <rect
                  x={x(g.from)}
                  width={Math.max(1, x(g.to) - x(g.from))}
                  y={PAD.t}
                  height={h() - PAD.t - PAD.b}
                  fill="url(#chart-hatch)"
                />
              )}
            </For>
          </g>
          <For each={yTicks()}>
            {(v) => (
              <>
                <line
                  class={v === 0 ? "zero" : "grid-l"}
                  x1={PAD.l}
                  x2={W - PAD.r}
                  y1={y(v)}
                  y2={y(v)}
                />
                <text x={PAD.l - 6} y={y(v) + 3} text-anchor="end">
                  {formatNumber(v, props.locale)}
                </text>
              </>
            )}
          </For>
          <text x={4} y={PAD.t + 2}>
            {props.unit}
          </text>
          <For each={xTicks()}>
            {(tk) => (
              <>
                <line
                  class="grid-l"
                  x1={x(tk.at.getTime())}
                  x2={x(tk.at.getTime())}
                  y1={h() - PAD.b}
                  y2={h() - PAD.b + 4}
                />
                <text x={x(tk.at.getTime())} y={h() - 8} text-anchor="middle">
                  {new Intl.DateTimeFormat(props.locale, tk.options).format(
                    tk.at,
                  )}
                </text>
              </>
            )}
          </For>
          <g clip-path={`url(#${clip})`}>
            <For each={bars()}>
              {(s, i) => (
                <For each={s.points.filter((p) => p.v !== null)}>
                  {(p) => {
                    const v = p.v!;
                    // A function, not a value: the position must follow zoom and pan like the width does.
                    const bx = () =>
                      x(p.t) +
                      barWidth() * bars().length * 0.125 +
                      i() * barWidth();
                    return (
                      <rect
                        x={bx()}
                        width={barWidth()}
                        y={Math.min(y(v), y(0))}
                        height={Math.max(0.5, Math.abs(y(v) - y(0)))}
                        fill={s.color}
                        rx="1"
                        opacity={faded(s.key) ? 0.2 : 1}
                      />
                    );
                  }}
                </For>
              )}
            </For>
            <For each={props.series.filter((s) => s.kind !== "bar")}>
              {(s) => (
                <g opacity={faded(s.key) ? 0.15 : 1}>
                  <Show when={s.kind === "area"}>
                    <path
                      d={path(s.points, true)}
                      fill={s.color}
                      opacity="0.14"
                    />
                  </Show>
                  <path
                    d={path(s.points, false)}
                    fill="none"
                    stroke={s.color}
                    stroke-width={s.dotted ? 1 : 1.7}
                    opacity={s.dotted ? 0.6 : 1}
                    stroke-dasharray={
                      s.dotted ? "2 3" : s.dashed ? "4 3" : undefined
                    }
                  />
                </g>
              )}
            </For>
          </g>
          <For each={props.thresholds ?? []}>
            {(th) => (
              <>
                <line
                  x1={PAD.l}
                  x2={W - PAD.r}
                  y1={y(th.value)}
                  y2={y(th.value)}
                  stroke="var(--crit)"
                  stroke-dasharray="5 4"
                  stroke-width="1.5"
                />
                <text
                  x={W - PAD.r - 2}
                  y={y(th.value) - 4}
                  text-anchor="end"
                  style={{ fill: "var(--crit)" }}
                >
                  {th.label}
                </text>
              </>
            )}
          </For>
          <For each={(props.markers ?? []).filter((m) => inside(m.at))}>
            {(m) => (
              <>
                <line
                  x1={x(m.at)}
                  x2={x(m.at)}
                  y1={PAD.t}
                  y2={h() - PAD.b}
                  stroke="var(--crit)"
                  stroke-dasharray="3 3"
                  opacity="0.55"
                  pointer-events="none"
                />
                <g
                  style={{ cursor: m.onClick ? "pointer" : undefined }}
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    m.onClick?.();
                  }}
                >
                  <title>{m.label}</title>
                  <path
                    d={`M${x(m.at) - 7} ${h() - PAD.b + 1} h14 l-7 9 z`}
                    fill="var(--crit)"
                  />
                </g>
              </>
            )}
          </For>
          <For each={(props.annotations ?? []).filter((a) => inside(a.at))}>
            {(a) => (
              <>
                <line
                  x1={x(a.at)}
                  x2={x(a.at)}
                  y1={PAD.t}
                  y2={h() - PAD.b}
                  stroke="var(--accent)"
                  opacity="0.7"
                  pointer-events="none"
                />
                <g pointer-events="none">
                  <rect
                    x={x(a.at) + 3}
                    y={PAD.t + 2}
                    width={Math.min(
                      170,
                      Math.min(26, a.text.length) * 6.3 + 12,
                    )}
                    height="16"
                    rx="3"
                    fill="var(--accent-soft)"
                  />
                  <text
                    x={x(a.at) + 9}
                    y={PAD.t + 13.5}
                    style={{ fill: "var(--accent)" }}
                  >
                    {a.text.length > 26 ? `${a.text.slice(0, 25)}…` : a.text}
                  </text>
                </g>
                <g>
                  <title>
                    {a.text} · {a.who}
                  </title>
                  <path
                    d={`M${x(a.at) - 6} ${h() - PAD.b + 1} h12 v8 h-12 z`}
                    fill="var(--accent)"
                  />
                </g>
              </>
            )}
          </For>
          <Show when={brush()}>
            {(b) => (
              <rect
                x={Math.max(PAD.l, Math.min(x(b().a), x(b().b)))}
                y={PAD.t}
                width={Math.abs(x(b().b) - x(b().a))}
                height={h() - PAD.t - PAD.b}
                fill="var(--accent)"
                opacity="0.15"
              />
            )}
          </Show>
          <Show
            when={
              props.now !== undefined && props.now > t0() && props.now < t1()
            }
          >
            <line
              x1={x(props.now!)}
              x2={x(props.now!)}
              y1={PAD.t}
              y2={h() - PAD.b}
              stroke="var(--accent)"
              stroke-dasharray="3 3"
            />
            <text
              x={x(props.now!) + 4}
              y={PAD.t + 8}
              style={{ fill: "var(--accent)" }}
            >
              {props.nowLabel}
            </text>
          </Show>
          <Show when={hover() !== null}>
            <line
              class="cursor"
              x1={x(hover()!)}
              x2={x(hover()!)}
              y1={PAD.t}
              y2={h() - PAD.b}
            />
          </Show>
        </svg>
        <Show when={tip()}>
          {(tp) => (
            <div
              class="tip"
              style={{ left: `${(x(tp().at) / W) * 100}%`, top: "0" }}
            >
              <div>{formatDateTime(new Date(tp().at), props.locale)}</div>
              <For each={tp().rows}>
                {(r) => (
                  <div>
                    <i
                      style={{
                        display: "inline-block",
                        width: "8px",
                        height: "8px",
                        background: r.s.color,
                        "margin-right": "6px",
                        "border-radius": "2px",
                      }}
                    />
                    {r.s.label}:{" "}
                    <b>
                      {r.p?.v == null ? "—" : formatNumber(r.p.v, props.locale)}
                    </b>{" "}
                    {r.p?.v == null ? "" : props.unit}{" "}
                    <span class="tq">
                      {r.p?.v == null
                        ? t("chart.quality.none")
                        : r.p.c !== undefined && r.p.c < 0.999
                          ? t("chart.quality.partial", {
                              share: formatShare(r.p.c, props.locale),
                            })
                          : t("chart.quality.measured")}
                    </span>
                  </div>
                )}
              </For>
              <Show when={tp().note}>
                {(n) => (
                  <div style={{ color: "var(--accent)" }}>✎ {n().text}</div>
                )}
              </Show>
              <Show when={props.mode === "note"}>
                <div class="tq">{t("chart.clickToNote")}</div>
              </Show>
            </div>
          )}
        </Show>
      </div>
      <Show when={!props.bare}>
        <div
          class="hint"
          style={{
            display: "flex",
            gap: "8px",
            "align-items": "center",
            "flex-wrap": "wrap",
            "padding-top": "6px",
          }}
        >
          <span class="chart-zoom" role="group" aria-label={t("chart.zoom")}>
            <button
              type="button"
              class="btn ghost sm"
              aria-label={t("chart.zoomOut")}
              title={t("chart.zoomOut")}
              disabled={!view()}
              onClick={() => zoomBy(2)}
            >
              −
            </button>
            <button
              type="button"
              class="btn ghost sm"
              aria-label={t("chart.zoomIn")}
              title={t("chart.zoomIn")}
              onClick={() => zoomBy(0.5)}
            >
              +
            </button>
            <Show when={view()}>
              <button
                type="button"
                class="btn ghost sm"
                aria-label={t("chart.earlier")}
                title={t("chart.earlier")}
                onClick={() => panBy(-1 / 3)}
              >
                ‹
              </button>
              <button
                type="button"
                class="btn ghost sm"
                aria-label={t("chart.later")}
                title={t("chart.later")}
                onClick={() => panBy(1 / 3)}
              >
                ›
              </button>
            </Show>
          </span>
          <Show when={view()} fallback={<span>{t("chart.zoomHint")}</span>}>
            {(v) => (
              <>
                <span>
                  {t("chart.zoomed", {
                    from: formatDateTime(new Date(v().from), props.locale),
                    to: formatDateTime(new Date(v().to), props.locale),
                  })}
                </span>
                <button
                  type="button"
                  class="btn ghost sm"
                  onClick={() => setView(null)}
                >
                  {t("chart.wholeRange")}
                </button>
              </>
            )}
          </Show>
        </div>
        <div class="legend" style={{ padding: "8px 0 0" }}>
          <For each={props.series}>
            {(s) => (
              <span>
                <i
                  class={
                    s.kind === "bar"
                      ? "sw box"
                      : `sw${s.dashed || s.dotted ? " dash" : ""}`
                  }
                  style={
                    s.kind === "bar"
                      ? { background: s.color }
                      : { "border-top-color": s.color }
                  }
                />
                {s.label}
              </span>
            )}
          </For>
          <Show when={(props.gaps?.length ?? 0) > 0 && props.gapLabel}>
            <span>
              <i
                class="sw box"
                style={{
                  background:
                    "repeating-linear-gradient(135deg,var(--hatch) 0 2px,transparent 2px 5px)",
                }}
              />
              {props.gapLabel}
            </span>
          </Show>
        </div>
      </Show>
    </Show>
  );
}

/**
 * The whole period in a strip under a chart (prototype "Nawigator"): the visible span is a window that can be
 * dragged, or moved by a click.
 */
export function Navigator(props: {
  points: ChartPoint[];
  from: Date;
  to: Date;
  view: Span | null;
  onView: (view: Span | null) => void;
  hint: string;
}) {
  const H = 42;
  const full = (): Span => ({
    from: props.from.getTime(),
    to: props.to.getTime(),
  });
  const x = (t: number) =>
    PAD.l +
    ((t - full().from) / (full().to - full().from || 1)) * (W - PAD.l - PAD.r);
  const vals = () => props.points.filter((p) => p.v !== null).map((p) => p.v!);
  const lo = () => Math.min(0, ...vals());
  const hi = () => Math.max(...vals(), lo() + 1);
  const y = (v: number) => 5 + (H - 10) * (1 - (v - lo()) / (hi() - lo() || 1));
  const line = () =>
    props.points
      .filter((p) => p.v !== null)
      .map((p) => `${x(p.t).toFixed(1)},${y(p.v!).toFixed(1)}`)
      .join(" ");
  let el: SVGSVGElement | undefined;
  let drag: { start: number; view: Span } | null = null;
  const timeAt = (clientX: number) => {
    const r = el!.getBoundingClientRect();
    const px = ((clientX - r.left) / r.width) * W;
    return (
      full().from +
      ((px - PAD.l) / (W - PAD.l - PAD.r)) * (full().to - full().from)
    );
  };
  const shown = () => props.view ?? full();
  const place = (center: number, len: number) => {
    const next = pan(
      { from: center - len / 2, to: center + len / 2 },
      full(),
      0,
    );
    props.onView(isFull(next, full()) ? null : next);
  };
  return (
    <div class="navw">
      <svg
        ref={el}
        viewBox={`0 0 ${W} ${H}`}
        class="nav-s"
        role="img"
        aria-label={props.hint}
        style={{
          width: "100%",
          height: "auto",
          display: "block",
          "touch-action": "none",
          cursor: "grab",
        }}
        onPointerDown={(e) => {
          try {
            el!.setPointerCapture(e.pointerId);
          } catch {
            // Not capturable: the drag still works while the pointer stays over the strip.
          }
          const at = timeAt(e.clientX);
          const v = shown();
          if (props.view && at >= v.from && at <= v.to) {
            drag = { start: at, view: v };
          } else {
            const len = props.view
              ? v.to - v.from
              : Math.max(3_600_000, (full().to - full().from) / 4);
            place(at, len);
            drag = {
              start: at,
              view: props.view ?? { from: at - len / 2, to: at + len / 2 },
            };
          }
        }}
        onPointerMove={(e) => {
          if (drag) {
            const next = pan(drag.view, full(), timeAt(e.clientX) - drag.start);
            props.onView(isFull(next, full()) ? null : next);
          }
        }}
        onPointerUp={() => (drag = null)}
      >
        <rect
          x={PAD.l}
          y="0"
          width={W - PAD.l - PAD.r}
          height={H}
          rx="3"
          fill="var(--surface-2)"
          stroke="var(--line)"
        />
        <polyline
          points={line()}
          fill="none"
          stroke="var(--faint)"
          stroke-width="1"
          pointer-events="none"
        />
        <rect
          x={x(shown().from)}
          y="1"
          width={Math.max(6, x(shown().to) - x(shown().from))}
          height={H - 2}
          fill="var(--accent)"
          fill-opacity="0.14"
          stroke="var(--accent)"
          stroke-width="1.5"
          rx="2"
          pointer-events="none"
        />
      </svg>
      <span class="hint">{props.hint}</span>
    </div>
  );
}

/** The chart as a PNG (prototype export): theme colours resolved, white or dark background as on screen. */
export function chartPng(svg: SVGSVGElement, name: string): void {
  const root = getComputedStyle(document.documentElement);
  const vb = svg.viewBox.baseVal;
  const width = vb.width || svg.clientWidth;
  const height = vb.height || svg.clientHeight;
  let xml = new XMLSerializer()
    .serializeToString(svg)
    .replace(
      /var\((--[\w-]+)\)/g,
      (_m, v: string) => root.getPropertyValue(v).trim() || "#888",
    );
  xml = xml.replace(
    /^<svg([^>]*)>/,
    (_m, a: string) =>
      `<svg${a.includes("xmlns=") ? "" : ' xmlns="http://www.w3.org/2000/svg"'}${a.replace(/\sstyle="[^"]*"/, "")} width="${width * 2}" height="${height * 2}"><style>text{font-family:IBM Plex Mono,Consolas,monospace;font-size:10px;fill:${root.getPropertyValue("--faint").trim()}}</style><rect width="100%" height="100%" fill="${root.getPropertyValue("--surface").trim()}"/>`,
  );
  const img = new Image();
  img.onload = () => {
    const canvas = document.createElement("canvas");
    canvas.width = width * 2;
    canvas.height = height * 2;
    canvas.getContext("2d")!.drawImage(img, 0, 0);
    canvas.toBlob((blob) => {
      if (blob) {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `${name}.png`;
        a.click();
        URL.revokeObjectURL(a.href);
      }
    });
  };
  img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(xml)}`;
}
